import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Paper,
    CircularProgress,
    Box,
    useTheme,
} from '@mui/material';
import MilkIcon from '@mui/icons-material/LocalDrink';
import StoreIcon from '@mui/icons-material/Store';
import CowMultipleIcon from '@mui/icons-material/Agriculture';
import CowBreedIcon from '@mui/icons-material/Pets';
import CheeseProductionIcon from '@mui/icons-material/BreakfastDining';

import api from '../services/api';

const iconMap = {
    totalLitersProduced24: <MilkIcon color="primary" sx={{ fontSize: 40 }} />,
    totalCows: <CowMultipleIcon color="secondary" sx={{ fontSize: 40 }} />,
    cowsPure: <CowBreedIcon color="success" sx={{ fontSize: 40 }} />,
    producesCheese: <CheeseProductionIcon color="warning" sx={{ fontSize: 40 }} />,
    sellsCheesemakers: <StoreIcon color="info" sx={{ fontSize: 40 }} />,
    sellsDairyCompanies: <StoreIcon color="info" sx={{ fontSize: 40 }} />,
    sellsOutsideMunicipality: <StoreIcon color="info" sx={{ fontSize: 40 }} />,
};

const ReportMilkProducerDialog = ({ open, onClose }) => {
    const theme = useTheme();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;

        setLoading(true);
        api
            .get('type-business/milk-producer-data')
            .then((res) => setReport(res.data))
            .finally(() => setLoading(false));
    }, [open]);

    const cardsData = [
        // Linha 1
        {
            key: 'totalLitersProduced24',
            title: 'Total de Litros Produzidos (24h)',
            description: 'Estimativa do total de Litros de leite produzidos em 24 horas',
            unit: 'L',
            highlight: true,
        },
        {
            key: 'totalCows',
            title: 'Total de Vacas',
            description: 'Estimativa do número total de vacas no munícipio',
        },
        {
            key: 'producesCheese',
            title: 'Produz Queijo',
            description: 'Total de produtores de leite que também fabricam queijo',
            // isBoolean removido
        },
        // Linha 2
        {
            key: 'sellsCheesemakers',
            title: 'Vende para Queijarias',
            description: 'Total de produtores de leite que vendem para queijarias',
            // isBoolean removido
        },
        {
            key: 'sellsDairyCompanies',
            title: 'Vende para Laticínios',
            description: 'Total de produtores de leite que vendem para grandes laticínios',
            // isBoolean removido
        },
        {
            key: 'sellsOutsideMunicipality',
            title: 'Vende para Outros Municípios',
            description: 'Total de produtores de leite que vendem para fora do município',
            // isBoolean removido
        },
    ];


    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: theme.palette.success.dark,
                    textAlign: 'center',
                    py: 2,
                    fontSize: '1.6rem',
                }}
            >
                Produção de Leite em Cedro-CE
            </DialogTitle>
            <DialogContent dividers sx={{ backgroundColor: '#fafafa', p: 4 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress color="success" />
                    </Box>
                )}
                {!loading && report && (
                    <Grid container spacing={4} justifyContent="center">
                        {cardsData.map(({ key, title, description, unit, highlight, isBoolean }) => {
                            const value = report[key];
                            const displayValue = isBoolean
                                ? value
                                    ? 'Sim'
                                    : 'Não'
                                : Number(value)?.toLocaleString('pt-BR') || '0';

                            return (
                                <Grid
                                    item
                                    key={key}
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    sx={{ display: 'flex', justifyContent: 'center' }}
                                >
                                    <Paper
                                        elevation={highlight ? 6 : 2}
                                        sx={{
                                            p: 3,
                                            width: '100%',
                                            maxWidth: 350,
                                            borderRadius: 2,
                                            borderTop: `6px solid ${
                                                highlight ? theme.palette.success.main : theme.palette.success.light
                                            }`,
                                            background: highlight
                                                ? `linear-gradient(135deg, ${theme.palette.success.light}33 0%, ${theme.palette.success.light}11 100%)`
                                                : '#fff',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            transition: 'transform 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: theme.shadows[6],
                                            },
                                        }}
                                    >
                                        <Box>{iconMap[key]}</Box>
                                        <Typography
                                            variant={highlight ? 'h3' : 'h4'}
                                            component="div"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: highlight ? theme.palette.success.dark : theme.palette.success.main,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {displayValue}
                                            {unit && (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '0.6em',
                                                        marginLeft: 0.7,
                                                        color: theme.palette.success.main,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {unit}
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            component="div"
                                            sx={{ fontWeight: 600, textAlign: 'center', color: '#444' }}
                                        >
                                            {title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ textAlign: 'center', color: '#666', fontSize: '0.9rem' }}
                                        >
                                            {description}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
                {!loading && !report && (
                    <Typography sx={{ textAlign: 'center', mt: 4, color: '#999' }}>
                        Nenhum dado disponível.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ backgroundColor: '#fafafa', px: 4, py: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="success"
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        letterSpacing: 0.5,
                    }}
                >
                    Fechar Relatório
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportMilkProducerDialog;